import { Bouton } from '../ui/composants/bouton'
import { useState } from 'react'
import type { Entreprise } from '../lib/entreprise'
import type { Impaye } from '../lib/impayes'
import { lienEmail, lienSms, LIBELLES_NIVEAU, messageRelance, niveauSuggere, noterRelance, type NiveauRelance, type Relance } from '../lib/relance'
import { getBase, type ClientLocal } from '../outbox/base'
import { Feuille } from '../ui/composants/feuille'
import { CHAMP } from '../ui/synchro'

/**
 * Relance d'un impayé : message préparé (rappel amiable ou relance ferme), modifiable, à transmettre par le canal
 * du téléphone. L'app n'envoie rien elle-même ; elle note la date de la relance dès qu'un canal est utilisé.
 */
export function RelanceImpaye(props: { impaye: Impaye; client?: ClientLocal; entreprise?: Entreprise; relances: Relance[]; fermer: () => void }) {
  const { impaye: i } = props
  const options = { professionnel: !!props.client?.siret, dejaRelance: props.relances.length > 0 }
  const [niveau, setNiveau] = useState<NiveauRelance>(niveauSuggere(i.joursRetard))
  const [corps, setCorps] = useState(() => messageRelance(i, props.entreprise, niveauSuggere(i.joursRetard), options).corps)
  const [info, setInfo] = useState<string | null>(null)
  const message = { ...messageRelance(i, props.entreprise, niveau, options), corps }
  const partageable = typeof navigator.share === 'function'

  const choisir = (n: NiveauRelance) => {
    setNiveau(n)
    setCorps(messageRelance(i, props.entreprise, n, options).corps) // le niveau change le fond du message : on le régénère
  }
  const noter = async (retour: string) => {
    await noterRelance(getBase(), i.id, niveau)
    setInfo(retour)
  }

  async function partager() {
    try {
      await navigator.share({ title: message.objet, text: message.corps })
      await noter('Relance partagée.')
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) setInfo('Le partage a échoué : copiez le message.')
    }
  }
  async function copier() {
    try {
      await navigator.clipboard.writeText(`${message.objet}\n\n${message.corps}`)
      await noter('Message copié.')
    } catch {
      setInfo('Copie impossible : sélectionnez le texte à la main.')
    }
  }

  return (
    <Feuille ouverte onChange={(ouverte) => !ouverte && props.fermer()} titre={`Relancer ${i.clientNom}`}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-slate-700">
          Rien n’est envoyé par l’app : relisez le message, puis transmettez-le.
          {props.relances.length > 0 && ` Déjà relancé ${props.relances.length} fois (dernière le ${new Date(props.relances.at(-1)!.date).toLocaleDateString('fr-FR')}).`}
        </p>

        <fieldset className="grid grid-cols-2 gap-2">
          <legend className="sr-only">Niveau de la relance</legend>
          {([1, 2] as const).map((n) => (
            <Bouton key={n} type="button" role="radio" aria-checked={niveau === n} variante={niveau === n ? 'principal' : 'secondaire'} onClick={() => choisir(n)}>
              {LIBELLES_NIVEAU[n]}
            </Bouton>
          ))}
        </fieldset>

        <p className="text-sm">
          <span className="text-slate-700">Objet : </span>
          <strong>{message.objet}</strong>
        </p>
        <textarea aria-label="Message de relance" className={`${CHAMP} min-h-72 py-2 leading-snug`} value={corps} onChange={(e) => setCorps(e.target.value)} />
        {info && (
          <p className="rounded-2xl bg-slate-100 p-3 text-sm" role="status">
            {info}
          </p>
        )}

        <div className="grid grid-cols-2 gap-2">
          {partageable && (
            <Bouton className="col-span-2" onClick={partager}>
              Partager…
            </Bouton>
          )}
          <Bouton asChild variante={partageable ? 'secondaire' : 'principal'}>
            <a href={lienEmail(message, props.client?.email)} onClick={() => void noter('Relance ouverte dans votre messagerie.')}>
              E-mail
            </a>
          </Bouton>
          {props.client?.telephone ? (
            <Bouton asChild variante="secondaire">
              <a href={lienSms(props.client.telephone, message)} onClick={() => void noter('Relance ouverte dans vos messages.')}>
                SMS
              </a>
            </Bouton>
          ) : (
            <Bouton variante="secondaire" disabled title="Pas de téléphone sur la fiche client">
              SMS
            </Bouton>
          )}
          <Bouton variante="secondaire" className="col-span-2" onClick={copier}>
            Copier le message
          </Bouton>
        </div>
      </div>
    </Feuille>
  )
}
